/**
 * Frame a portrait before uploading it: drag to move, zoom with the slider, the wheel
 * or a pinch, and keep the square shown inside the circle. Its own dialog, so it also
 * works over an open modal (student form). Resolves with a square JPEG File, or null
 * when cancelled.
 */
const PhotoCropper = {
    SIZE: 512, // Output side in pixels; enough for every avatar in the app.

    open(file) {
        return new Promise(resolve => {
            const url = URL.createObjectURL(file);
            const image = new Image();
            image.onerror = () => { URL.revokeObjectURL(url); Utils.showToast('No se pudo abrir la imagen.', 'error'); resolve(null); };
            image.onload = () => this.show(image, url, resolve);
            image.src = url;
        });
    },

    show(image, url, resolve) {
        const previousFocus = document.activeElement;
        const root = document.createElement('div');
        root.className = 'photo-cropper-overlay';
        SafeHTML.set(root, `
            <div class="photo-cropper" role="dialog" aria-modal="true" aria-labelledby="photo-cropper-title">
                <h3 id="photo-cropper-title">Encuadra la foto</h3>
                <p class="photo-cropper-help">Arrastra la imagen para moverla y usa el zoom para acercarla. Se guardará lo que quede dentro del círculo.</p>
                <div class="photo-cropper-stage" tabindex="0" aria-label="Zona de encuadre. Usa las flechas para mover la imagen y + o − para el zoom.">
                    <img alt="" draggable="false">
                    <div class="photo-cropper-mask" aria-hidden="true"></div>
                </div>
                <label class="photo-cropper-zoom">
                    <i class="fas fa-magnifying-glass-minus" aria-hidden="true"></i>
                    <input type="range" min="1" max="4" step="0.01" value="1" aria-label="Zoom">
                    <i class="fas fa-magnifying-glass-plus" aria-hidden="true"></i>
                </label>
                <div class="photo-cropper-actions">
                    <button type="button" class="btn btn-secondary" data-crop="cancel">Cancelar</button>
                    <button type="button" class="btn btn-primary" data-crop="save"><i class="fas fa-check"></i> Usar esta foto</button>
                </div>
            </div>`);
        document.body.appendChild(root);
        const stage = root.querySelector('.photo-cropper-stage');
        const img = root.querySelector('img');
        const slider = root.querySelector('input[type="range"]');
        img.src = url;

        const view = stage.clientWidth;
        // Zoom 1 = the image just covers the circle's square.
        const base = view / Math.min(image.naturalWidth, image.naturalHeight);
        const state = { zoom: 1, x: 0, y: 0 }; // x, y: offset of the image centre from the stage centre.
        const clamp = () => {
            const scale = base * state.zoom;
            const maxX = Math.max(0, (image.naturalWidth * scale - view) / 2);
            const maxY = Math.max(0, (image.naturalHeight * scale - view) / 2);
            state.x = Math.min(maxX, Math.max(-maxX, state.x));
            state.y = Math.min(maxY, Math.max(-maxY, state.y));
        };
        const draw = () => {
            clamp();
            const scale = base * state.zoom;
            img.style.width = `${image.naturalWidth * scale}px`;
            img.style.height = `${image.naturalHeight * scale}px`;
            img.style.transform = `translate(calc(-50% + ${state.x}px), calc(-50% + ${state.y}px))`;
        };
        const setZoom = zoom => {
            const next = Math.min(4, Math.max(1, zoom));
            // Keep the point at the centre of the circle where it is.
            state.x *= next / state.zoom; state.y *= next / state.zoom;
            state.zoom = next; slider.value = String(next);
            draw();
        };

        const pointers = new Map();
        let pinch = null;
        stage.addEventListener('pointerdown', event => {
            stage.setPointerCapture(event.pointerId);
            pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            if (pointers.size === 2) {
                const [a, b] = [...pointers.values()];
                pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y), zoom: state.zoom };
            }
        });
        stage.addEventListener('pointermove', event => {
            const last = pointers.get(event.pointerId);
            if (!last) return;
            pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            if (pointers.size === 2 && pinch) {
                const [a, b] = [...pointers.values()];
                setZoom(pinch.zoom * Math.hypot(a.x - b.x, a.y - b.y) / pinch.distance);
            } else if (pointers.size === 1) {
                state.x += event.clientX - last.x; state.y += event.clientY - last.y;
                draw();
            }
        });
        const release = event => { pointers.delete(event.pointerId); if (pointers.size < 2) pinch = null; };
        stage.addEventListener('pointerup', release);
        stage.addEventListener('pointercancel', release);
        stage.addEventListener('wheel', event => { event.preventDefault(); setZoom(state.zoom * (event.deltaY < 0 ? 1.08 : 1 / 1.08)); }, { passive: false });
        slider.addEventListener('input', () => setZoom(Number(slider.value)));

        const finish = result => {
            document.removeEventListener('keydown', onKey, true);
            root.remove();
            URL.revokeObjectURL(url);
            previousFocus?.focus?.();
            resolve(result);
        };
        const save = () => {
            const canvas = document.createElement('canvas');
            canvas.width = canvas.height = this.SIZE;
            const context = canvas.getContext('2d');
            context.fillStyle = '#fff'; // Transparent PNGs become JPEG.
            context.fillRect(0, 0, this.SIZE, this.SIZE);
            const scale = base * state.zoom;
            // Square of the source image under the circle.
            const side = view / scale;
            const sx = image.naturalWidth / 2 - state.x / scale - side / 2;
            const sy = image.naturalHeight / 2 - state.y / scale - side / 2;
            context.drawImage(image, sx, sy, side, side, 0, 0, this.SIZE, this.SIZE);
            canvas.toBlob(blob => {
                if (!blob) { Utils.showToast('No se pudo preparar la foto.', 'error'); return; }
                finish(new File([blob], 'foto.jpg', { type: 'image/jpeg' }));
            }, 'image/jpeg', 0.9);
        };
        const onKey = event => {
            if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); finish(null); return; }
            if (event.key === 'Tab') { // Keep focus inside the dialog.
                const focusable = [...root.querySelectorAll('[tabindex="0"], input, button')];
                const index = focusable.indexOf(document.activeElement);
                const next = event.shiftKey ? (index <= 0 ? focusable.length - 1 : index - 1) : (index + 1) % focusable.length;
                event.preventDefault(); focusable[next].focus();
                return;
            }
            if (document.activeElement !== stage) return;
            const moves = { ArrowLeft: [10, 0], ArrowRight: [-10, 0], ArrowUp: [0, 10], ArrowDown: [0, -10] };
            if (moves[event.key]) { event.preventDefault(); state.x += moves[event.key][0]; state.y += moves[event.key][1]; draw(); }
            else if (event.key === '+' || event.key === '=') { event.preventDefault(); setZoom(state.zoom * 1.1); }
            else if (event.key === '-') { event.preventDefault(); setZoom(state.zoom / 1.1); }
        };
        document.addEventListener('keydown', onKey, true);
        root.querySelector('[data-crop="cancel"]').addEventListener('click', () => finish(null));
        root.querySelector('[data-crop="save"]').addEventListener('click', save);
        draw();
        stage.focus();
    }
};
